import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Mail, Inbox, Send, RefreshCw, CheckCircle2, XCircle, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";

const PASSWORD_MASK = "***";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/**
 * Email Settings card — admin-only configuration for inbound mailbox
 * (IMAP/POP3) and outbound SMTP. Embedded inside the Settings page.
 */
export default function EmailSettings({ isAdmin }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingIn, setTestingIn] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [smtpTestTo, setSmtpTestTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/email-config");
      setCfg(data);
      setSmtpTestTo(data.smtp_from_email || "");
    } catch (e) {
      // Non-admin users can't read this — that's fine; we just hide the card.
      setCfg(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));

  const save = async () => {
    if (!isAdmin || !cfg) return;
    setSaving(true);
    try {
      // Send everything; mask values are dropped server-side.
      const body = {
        enabled: !!cfg.enabled,
        inbound_protocol: cfg.inbound_protocol || "",
        inbound_host: cfg.inbound_host || "",
        inbound_port: Number(cfg.inbound_port) || 0,
        inbound_use_ssl: !!cfg.inbound_use_ssl,
        inbound_user: cfg.inbound_user || "",
        inbound_password: cfg.inbound_password || "",
        inbound_folder: cfg.inbound_folder || "INBOX",
        inbound_poll_minutes: Number(cfg.inbound_poll_minutes) || 5,
        smtp_host: cfg.smtp_host || "",
        smtp_port: Number(cfg.smtp_port) || 0,
        smtp_security: cfg.smtp_security || "starttls",
        smtp_user: cfg.smtp_user || "",
        smtp_password: cfg.smtp_password || "",
        smtp_from_email: cfg.smtp_from_email || "",
        smtp_from_name: cfg.smtp_from_name || "",
        auto_reply_enabled: !!cfg.auto_reply_enabled,
        auto_reply_subject: cfg.auto_reply_subject || "",
        auto_reply_body: cfg.auto_reply_body || "",
        new_ticket_enabled: !!cfg.new_ticket_enabled,
        new_ticket_subject: cfg.new_ticket_subject || "",
        new_ticket_body: cfg.new_ticket_body || "",
      };
      const { data } = await api.patch("/email-config", body);
      setCfg(data);
      toast.success("Email settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const testInbound = async () => {
    setTestingIn(true);
    try {
      await api.post("/email-config/test-inbound");
      toast.success("Inbound mailbox connected");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Inbound test failed");
    } finally {
      setTestingIn(false);
    }
  };

  const testSmtp = async () => {
    if (!smtpTestTo.trim()) {
      toast.error("Enter a recipient for the test email");
      return;
    }
    setTestingSmtp(true);
    try {
      await api.post("/email-config/test-smtp", { to: smtpTestTo.trim() });
      toast.success(`Test email sent to ${smtpTestTo.trim()}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "SMTP test failed");
    } finally {
      setTestingSmtp(false);
    }
  };

  const fetchNow = async () => {
    setFetching(true);
    try {
      const { data } = await api.post("/email-config/fetch-now");
      if (data.skipped) {
        toast.info("Email integration is disabled");
      } else if (data.ok === false) {
        toast.error(`Fetch failed: ${data.error || "unknown"}`);
      } else {
        toast.success(`Fetched ${data.fetched} — ${data.created} new ticket(s), ${data.appended} reply(ies)`);
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Fetch failed");
    } finally {
      setFetching(false);
    }
  };

  if (loading) {
    return (
      <section className="bg-zinc-900 border border-white/10 rounded-lg p-5">
        <div className="flex items-center gap-2 text-zinc-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading email settings…
        </div>
      </section>
    );
  }
  if (!cfg) return null;
  if (!isAdmin) {
    return (
      <section className="bg-zinc-900 border border-white/10 rounded-lg p-5">
        <div className="label-overline mb-2 flex items-center gap-2">
          <Mail className="w-3.5 h-3.5" /> Email Settings
        </div>
        <p className="text-xs text-amber-400">Only admins can view or modify email integration.</p>
      </section>
    );
  }

  const lastBadge =
    cfg.last_poll_status === "ok" ? (
      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400">
        <CheckCircle2 className="w-3 h-3" /> {cfg.last_poll_status}
      </span>
    ) : cfg.last_poll_status === "error" ? (
      <span className="inline-flex items-center gap-1 text-[11px] text-red-400">
        <XCircle className="w-3 h-3" /> {cfg.last_poll_status}
      </span>
    ) : cfg.last_poll_status ? (
      <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500">
        {cfg.last_poll_status}
      </span>
    ) : null;

  return (
    <section className="bg-zinc-900 border border-white/10 rounded-lg p-5 space-y-6" data-testid="email-settings">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-overline mb-1 flex items-center gap-2">
            <Mail className="w-3.5 h-3.5" /> Email Settings
          </div>
          <p className="text-xs text-zinc-500 mt-1 max-w-2xl leading-relaxed">
            Configure inbound mailbox (IMAP / POP3) so emails automatically become tickets,
            and SMTP so ticket replies are mirrored to the contact&apos;s email address.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Label htmlFor="email-enabled" className="text-xs text-zinc-300">
            {cfg.enabled ? "Enabled" : "Disabled"}
          </Label>
          <Switch
            id="email-enabled"
            checked={!!cfg.enabled}
            onCheckedChange={(v) => set({ enabled: v })}
            data-testid="email-enabled-switch"
          />
        </div>
      </div>

      {/* ---------------- Inbound ---------------- */}
      <div className="rounded-md border border-white/10 bg-zinc-950 p-4 space-y-4" data-testid="email-inbound-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 text-zinc-200 font-medium">
            <Inbox className="w-4 h-4 text-sky-400" /> Inbound — Email to Ticket
          </div>
          {lastBadge}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-zinc-400">Protocol</Label>
            <Select
              value={cfg.inbound_protocol || "__none__"}
              onValueChange={(v) => set({ inbound_protocol: v === "__none__" ? "" : v })}
            >
              <SelectTrigger className="mt-1.5 bg-zinc-900 border-white/10" data-testid="email-inbound-protocol">
                <SelectValue placeholder="Disabled" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-white/10 text-zinc-100">
                <SelectItem value="__none__">Disabled</SelectItem>
                <SelectItem value="imap">IMAP</SelectItem>
                <SelectItem value="pop3">POP3</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Host</Label>
            <Input
              data-testid="email-inbound-host"
              value={cfg.inbound_host || ""}
              onChange={(e) => set({ inbound_host: e.target.value })}
              placeholder="imap.example.com"
              className="mt-1.5 bg-zinc-900 border-white/10"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Port</Label>
            <Input
              data-testid="email-inbound-port"
              type="number"
              value={cfg.inbound_port || ""}
              onChange={(e) => set({ inbound_port: e.target.value })}
              placeholder="993"
              className="mt-1.5 bg-zinc-900 border-white/10"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Username</Label>
            <Input
              data-testid="email-inbound-user"
              value={cfg.inbound_user || ""}
              onChange={(e) => set({ inbound_user: e.target.value })}
              placeholder="tickets@example.com"
              className="mt-1.5 bg-zinc-900 border-white/10"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Password</Label>
            <Input
              data-testid="email-inbound-password"
              type="password"
              value={cfg.inbound_password || ""}
              onChange={(e) => set({ inbound_password: e.target.value })}
              placeholder={cfg.inbound_password === PASSWORD_MASK ? "Already set" : ""}
              className="mt-1.5 bg-zinc-900 border-white/10"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Folder (IMAP)</Label>
            <Input
              data-testid="email-inbound-folder"
              value={cfg.inbound_folder || ""}
              onChange={(e) => set({ inbound_folder: e.target.value })}
              placeholder="INBOX"
              className="mt-1.5 bg-zinc-900 border-white/10"
            />
          </div>
          <div className="flex items-center gap-3 md:col-span-1">
            <Switch
              checked={!!cfg.inbound_use_ssl}
              onCheckedChange={(v) => set({ inbound_use_ssl: v })}
              id="email-inbound-ssl"
              data-testid="email-inbound-ssl"
            />
            <Label htmlFor="email-inbound-ssl" className="text-xs text-zinc-300">Use SSL/TLS</Label>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs text-zinc-400">Poll every (minutes, 1–60)</Label>
            <Input
              data-testid="email-inbound-poll-minutes"
              type="number"
              min={1}
              max={60}
              value={cfg.inbound_poll_minutes || ""}
              onChange={(e) => set({ inbound_poll_minutes: e.target.value })}
              className="mt-1.5 bg-zinc-900 border-white/10 max-w-[160px]"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2 border-t border-white/5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={testInbound}
            disabled={testingIn || !cfg.inbound_protocol || !cfg.inbound_host}
            data-testid="email-inbound-test"
            className="border-white/15 bg-zinc-900 text-zinc-200 hover:bg-zinc-800"
          >
            {testingIn ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
            Test Connection
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={fetchNow}
            disabled={fetching || !cfg.enabled}
            data-testid="email-inbound-fetch-now"
            className="border-white/15 bg-zinc-900 text-zinc-200 hover:bg-zinc-800"
          >
            {fetching ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> :
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            Fetch Now
          </Button>
          {cfg.last_poll_at && (
            <span className="text-[11px] text-zinc-500 ml-2 truncate" data-testid="email-last-poll">
              Last: {fmtDate(cfg.last_poll_at)}
              {cfg.last_poll_message ? ` — ${cfg.last_poll_message}` : ""}
            </span>
          )}
        </div>
      </div>

      {/* ---------------- Outbound (SMTP) ---------------- */}
      <div className="rounded-md border border-white/10 bg-zinc-950 p-4 space-y-4" data-testid="email-smtp-card">
        <div className="flex items-center gap-2 text-zinc-200 font-medium">
          <Send className="w-4 h-4 text-emerald-400" /> Outbound — SMTP (Ticket Replies → Email)
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs text-zinc-400">SMTP Host</Label>
            <Input
              data-testid="email-smtp-host"
              value={cfg.smtp_host || ""}
              onChange={(e) => set({ smtp_host: e.target.value })}
              placeholder="smtp.example.com"
              className="mt-1.5 bg-zinc-900 border-white/10"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Port</Label>
            <Input
              data-testid="email-smtp-port"
              type="number"
              value={cfg.smtp_port || ""}
              onChange={(e) => set({ smtp_port: e.target.value })}
              placeholder="587"
              className="mt-1.5 bg-zinc-900 border-white/10"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">SMTP Username</Label>
            <Input
              data-testid="email-smtp-user"
              value={cfg.smtp_user || ""}
              onChange={(e) => set({ smtp_user: e.target.value })}
              placeholder="user@example.com"
              className="mt-1.5 bg-zinc-900 border-white/10"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">SMTP Password</Label>
            <Input
              data-testid="email-smtp-password"
              type="password"
              value={cfg.smtp_password || ""}
              onChange={(e) => set({ smtp_password: e.target.value })}
              placeholder={cfg.smtp_password === PASSWORD_MASK ? "Already set" : ""}
              className="mt-1.5 bg-zinc-900 border-white/10"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Security</Label>
            <Select
              value={cfg.smtp_security || "starttls"}
              onValueChange={(v) => set({ smtp_security: v })}
            >
              <SelectTrigger className="mt-1.5 bg-zinc-900 border-white/10" data-testid="email-smtp-security">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-white/10 text-zinc-100">
                <SelectItem value="none">None (port 25)</SelectItem>
                <SelectItem value="starttls">STARTTLS (port 587)</SelectItem>
                <SelectItem value="ssl">SSL / TLS (port 465)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">From Email</Label>
            <Input
              data-testid="email-smtp-from-email"
              value={cfg.smtp_from_email || ""}
              onChange={(e) => set({ smtp_from_email: e.target.value })}
              placeholder="support@yourcompany.com"
              className="mt-1.5 bg-zinc-900 border-white/10"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs text-zinc-400">From Name (optional)</Label>
            <Input
              data-testid="email-smtp-from-name"
              value={cfg.smtp_from_name || ""}
              onChange={(e) => set({ smtp_from_name: e.target.value })}
              placeholder="Your Company Support"
              className="mt-1.5 bg-zinc-900 border-white/10"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2 border-t border-white/5 flex-wrap">
          <Input
            data-testid="email-smtp-test-to"
            value={smtpTestTo}
            onChange={(e) => setSmtpTestTo(e.target.value)}
            placeholder="recipient@example.com"
            className="bg-zinc-900 border-white/10 max-w-[260px]"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={testSmtp}
            disabled={testingSmtp || !cfg.smtp_host || !cfg.smtp_from_email}
            data-testid="email-smtp-test"
            className="border-white/15 bg-zinc-900 text-zinc-200 hover:bg-zinc-800"
          >
            {testingSmtp ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
            Send Test Email
          </Button>
        </div>
      </div>

      {/* ---------------- Auto-Reply Template ---------------- */}
      <div className="rounded-md border border-white/10 bg-zinc-950 p-4 space-y-4" data-testid="email-autoreply-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 text-zinc-200 font-medium">
            <MessageSquare className="w-4 h-4 text-sky-400" /> Auto-Reply — Ticket Acknowledgement
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="email-autoreply-enabled" className="text-xs text-zinc-300">
              {cfg.auto_reply_enabled ? "Enabled" : "Disabled"}
            </Label>
            <Switch
              id="email-autoreply-enabled"
              checked={!!cfg.auto_reply_enabled}
              onCheckedChange={(v) => set({ auto_reply_enabled: v })}
              data-testid="email-autoreply-enabled"
            />
          </div>
        </div>

        <p className="text-[11px] text-zinc-500 leading-relaxed">
          Sent immediately to the sender whenever an inbound email creates a new ticket.
          Placeholders: <span className="font-mono text-zinc-300">{"{ticket_code}"}</span>,{" "}
          <span className="font-mono text-zinc-300">{"{subject}"}</span>,{" "}
          <span className="font-mono text-zinc-300">{"{sender_name}"}</span>,{" "}
          <span className="font-mono text-zinc-300">{"{sender_email}"}</span>,{" "}
          <span className="font-mono text-zinc-300">{"{description}"}</span>.
        </p>

        <div>
          <Label className="text-xs text-zinc-400">Subject Template</Label>
          <Input
            data-testid="email-autoreply-subject"
            value={cfg.auto_reply_subject || ""}
            onChange={(e) => set({ auto_reply_subject: e.target.value })}
            placeholder="[{ticket_code}] We received your message — {subject}"
            disabled={!cfg.auto_reply_enabled}
            className="mt-1.5 bg-zinc-900 border-white/10"
          />
        </div>

        <div>
          <Label className="text-xs text-zinc-400">Body Template</Label>
          <Textarea
            data-testid="email-autoreply-body"
            value={cfg.auto_reply_body || ""}
            onChange={(e) => set({ auto_reply_body: e.target.value })}
            rows={8}
            disabled={!cfg.auto_reply_enabled}
            placeholder={`Hi {sender_name},\n\nThank you for reaching out. Your request has been received and assigned ticket number {ticket_code}.\n\nSubject: {subject}\n\nOur team will get back to you shortly.\n\n— Support`}
            className="mt-1.5 bg-zinc-950 border-white/10 text-zinc-100 font-mono text-sm"
          />
        </div>
      </div>

      {/* ---------------- New Ticket Notification Template ---------------- */}
      <div className="rounded-md border border-white/10 bg-zinc-950 p-4 space-y-4" data-testid="email-newticket-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 text-zinc-200 font-medium">
            <MessageSquare className="w-4 h-4 text-emerald-400" /> New Ticket — Contact Notification
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="email-newticket-enabled" className="text-xs text-zinc-300">
              {cfg.new_ticket_enabled ? "Enabled" : "Disabled"}
            </Label>
            <Switch
              id="email-newticket-enabled"
              checked={!!cfg.new_ticket_enabled}
              onCheckedChange={(v) => set({ new_ticket_enabled: v })}
              data-testid="email-newticket-enabled"
            />
          </div>
        </div>

        <p className="text-[11px] text-zinc-500 leading-relaxed">
          Sent to every contact email on a ticket the moment it&apos;s created via the console.
          Placeholders: <span className="font-mono text-zinc-300">{"{ticket_code}"}</span>,{" "}
          <span className="font-mono text-zinc-300">{"{subject}"}</span>,{" "}
          <span className="font-mono text-zinc-300">{"{description}"}</span>,{" "}
          <span className="font-mono text-zinc-300">{"{contact_name}"}</span>,{" "}
          <span className="font-mono text-zinc-300">{"{creator_name}"}</span>.
        </p>

        <div>
          <Label className="text-xs text-zinc-400">Subject Template</Label>
          <Input
            data-testid="email-newticket-subject"
            value={cfg.new_ticket_subject || ""}
            onChange={(e) => set({ new_ticket_subject: e.target.value })}
            placeholder="[{ticket_code}] New ticket raised — {subject}"
            disabled={!cfg.new_ticket_enabled}
            className="mt-1.5 bg-zinc-900 border-white/10"
          />
        </div>

        <div>
          <Label className="text-xs text-zinc-400">Body Template</Label>
          <Textarea
            data-testid="email-newticket-body"
            value={cfg.new_ticket_body || ""}
            onChange={(e) => set({ new_ticket_body: e.target.value })}
            rows={8}
            disabled={!cfg.new_ticket_enabled}
            placeholder={`Hi {contact_name},\n\nA new support ticket has been raised for you.\n\nTicket: {ticket_code}\nSubject: {subject}\n\n{description}\n\n— {creator_name}`}
            className="mt-1.5 bg-zinc-950 border-white/10 text-zinc-100 font-mono text-sm"
          />
        </div>
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-white/5">
        <p className="text-[11px] text-zinc-500">
          Passwords show as <span className="font-mono text-zinc-300">***</span> after saving — leave them as-is to keep the stored value.
        </p>
        <Button
          onClick={save}
          disabled={saving}
          data-testid="email-settings-save"
          className="bg-white text-black hover:bg-zinc-200"
        >
          {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
          Save Email Settings
        </Button>
      </div>
    </section>
  );
}
